import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Loader2, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "", organisation: "", position: "", email: "", phone: "",
  country_region: "", participants: "", support_type: "", context: "",
  delivery_format: "", timing: "", additional: "",
};

const SUPPORT_OPTIONS = [
  "Executive and leadership briefings",
  "Individual or group advisory sessions",
  "Team coaching and mentoring",
  "Facilitated workshops and project clinics",
  "Training tailored to organisational or project needs",
  "Review of live project work, systems or documentation",
  "Social performance tools, templates and frameworks",
  "Capability assessments and development plans",
  "Ongoing advisory support across project phases",
  "Blended program (multiple of the above)",
];

const DELIVERY_OPTIONS = ["In-person", "Online", "Blended (in-person and online)", "Not sure - open to advice"];

const TIMING_OPTIONS = ["Immediate (next 4 weeks)", "Next quarter", "Next 6 months", "12-month horizon", "Scoping / no fixed timing"];

export default function EnterpriseInquiry() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const req of ["name", "organisation", "email"]) {
      if (!form[req]) { toast.error("Please complete all required fields."); return; }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enterprise-inquiries`, form);
      setDone(true);
      toast.success("Thank you. Andrea will be in touch within two business days.");
      setForm(initial);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-3 border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue rounded-sm";
  const labelCls = "block text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 mb-2";

  return (
    <div data-testid="enterprise-inquiry-page">
      <section className="bg-brand-blue text-white py-20 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Link to="/shop" className="text-xs uppercase tracking-widest font-semibold text-brand-yellow hover:text-white inline-flex items-center gap-1.5 mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Back to shop</Link>
          <div className="eyebrow text-brand-yellow mb-6 inline-flex items-center gap-2"><Building2 className="w-4 h-4" /> For organisations and teams</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">Enterprise advisory and capability support.</h1>
          <p className="text-lg lg:text-xl text-white/85 max-w-3xl mt-6 leading-relaxed">
            Tell us about the team, the project context and the capability you're building. Andrea will come back within two business days to suggest the shape of a tailored program.
          </p>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          {done ? (
            <div data-testid="enterprise-inquiry-success" className="bg-white border border-slate-200 p-12 text-center max-w-2xl mx-auto">
              <div className="w-14 h-14 rounded-full bg-brand-orange/10 text-brand-orange flex items-center justify-center mx-auto mb-5">
                <Check className="w-7 h-7" />
              </div>
              <h2 className="font-serif text-3xl text-brand-blue mb-3">Enterprise inquiry received.</h2>
              <p className="text-slate-600 max-w-md mx-auto leading-relaxed mb-6">Andrea will read what you've sent, think about the shape of a program that fits, and come back within two business days.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/shop" className="btn-secondary">Back to shop</Link>
                <button data-testid="enterprise-reset-btn" onClick={() => setDone(false)} className="btn-primary">Submit another inquiry</button>
              </div>
            </div>
          ) : (
            <form data-testid="enterprise-inquiry-form" onSubmit={submit} className="bg-white border border-slate-200 p-8 lg:p-12 shadow-sm">
              <div className="mb-8 pb-6 border-b border-slate-200">
                <div className="eyebrow text-brand-orange mb-2">Inquiry category</div>
                <div className="text-slate-800 font-semibold" data-testid="enterprise-category">Enterprise advisory and capability support</div>
                <p className="text-xs text-slate-500 mt-2">If your inquiry relates to a defined project, approval, due diligence or independent review, please use <Link to="/contact" className="text-brand-orange font-semibold hover:underline">Contact Us</Link> instead.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Name*</label>
                  <input data-testid="ent-name" className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} required />
                </div>
                <div>
                  <label className={labelCls}>Organisation*</label>
                  <input data-testid="ent-org" className={inputCls} value={form.organisation} onChange={(e) => update("organisation", e.target.value)} required />
                </div>
                <div>
                  <label className={labelCls}>Position</label>
                  <input data-testid="ent-position" className={inputCls} value={form.position} onChange={(e) => update("position", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Email*</label>
                  <input data-testid="ent-email" type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} required />
                </div>
                <div>
                  <label className={labelCls}>Phone number</label>
                  <input data-testid="ent-phone" className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Country or region</label>
                  <input data-testid="ent-country" className={inputCls} placeholder="e.g. Australia · Queensland" value={form.country_region} onChange={(e) => update("country_region", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Approximate number of participants</label>
                  <input data-testid="ent-participants" className={inputCls} placeholder="e.g. 6-8 senior leaders" value={form.participants} onChange={(e) => update("participants", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Type of support required</label>
                  <select data-testid="ent-support-type" className={inputCls} value={form.support_type} onChange={(e) => update("support_type", e.target.value)}>
                    <option value="">Select the closest fit</option>
                    {SUPPORT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Preferred delivery format</label>
                  <select data-testid="ent-delivery" className={inputCls} value={form.delivery_format} onChange={(e) => update("delivery_format", e.target.value)}>
                    <option value="">Select preference</option>
                    {DELIVERY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Expected timing</label>
                  <select data-testid="ent-timing" className={inputCls} value={form.timing} onChange={(e) => update("timing", e.target.value)}>
                    <option value="">Select timing</option>
                    {TIMING_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Organisational or project context</label>
                  <textarea data-testid="ent-context" rows={4} className={inputCls} placeholder="A few lines on the team, project, sector or capability challenge." value={form.context} onChange={(e) => update("context", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Additional information</label>
                  <textarea data-testid="ent-additional" rows={4} className={inputCls} placeholder="Anything else Andrea should know before responding." value={form.additional} onChange={(e) => update("additional", e.target.value)} />
                </div>
              </div>

              <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <p className="text-xs text-slate-500 max-w-md">All inquiries are treated as confidential. Submissions are reviewed personally by Andrea Kanaris.</p>
                <button data-testid="enterprise-submit-btn" type="submit" disabled={loading} className="btn-primary min-w-[260px] justify-center">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send enterprise inquiry <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
